import { useState } from "react";
import { IInboundEmailCommunication } from "shared";
import { ArrowUturnLeftIcon } from "@heroicons/react/20/solid";
import { Tooltip } from "@mui/material";

import { CustomIframe } from "../../../../IFrame";

interface InboundEmailCommunicationProps {
  inboundEmail: IInboundEmailCommunication;
  showOriginalMessageLink?: boolean;
  onViewOriginalMessage?: () => void;
  isReplyOpen?: boolean;
  onReplyByEmail?: () => void;
  onDownloadAttachment?: (attachmentIndex: number, attachmentName: string) => void;
  downloadingAttachmentIndex?: number | null;
  /** Offers the whole email below the reply text. Only pass this where htmlBody arrives sanitised */
  showFullEmail?: boolean;
}

export function InboundEmailCommunication({
  inboundEmail,
  showOriginalMessageLink = false,
  onViewOriginalMessage,
  isReplyOpen = false,
  onReplyByEmail,
  onDownloadAttachment,
  downloadingAttachmentIndex = null,
  showFullEmail = false,
}: InboundEmailCommunicationProps) {
  const [fullEmailOpen, setFullEmailOpen] = useState(false);
  const createdAt = inboundEmail.createdAt ? new Date(inboundEmail.createdAt) : null;
  const attachmentNames = inboundEmail.attachmentNames || [];
  const attachments = inboundEmail.attachments?.length
    ? inboundEmail.attachments
    : attachmentNames.map((name, index) => ({
      index,
      name,
    }));
  const hasExtractedReplyText = !!inboundEmail.extractedReplyText?.trim();

  return (
    <div className={ `flex justify-start` }>
      <div className={ `max-w-3xl` }>
        <div className={ `flex items-center justify-between gap-2 mb-1` }>
          <div className={ `flex items-center gap-2` }>
            <span className={ `px-2 py-0.5 text-[10px] font-medium rounded-full border text-teal-700 bg-teal-100 border-teal-200` }>
              { `Email Reply Received` }
            </span>
            <div className={ `text-xs text-gray-500` }>
              { createdAt ? createdAt.toLocaleString() : `` }
            </div>
          </div>
          { onReplyByEmail ? (
            <Tooltip title={ isReplyOpen ? `Close reply` : `Reply by email` }>
              <button
                type={ `button` }
                aria-label={ isReplyOpen ? `Close reply` : `Reply by email` }
                aria-pressed={ isReplyOpen }
                className={ `flex h-7 w-7 items-center justify-center rounded-full border transition-colors ${isReplyOpen ? `border-teal-300 bg-teal-100 text-teal-800` : `border-transparent text-teal-700 hover:bg-teal-100`}` }
                onClick={ onReplyByEmail }
              >
                <ArrowUturnLeftIcon className={ `h-4 w-4` } />
              </button>
            </Tooltip>
          ) : null }
        </div>
        <div className={ `rounded-2xl rounded-tl-md bg-teal-50 border border-teal-200 shadow-xs overflow-hidden` }>
          <div className={ `px-4 py-3` }>
            <div className={ `text-xs text-gray-500` }>
              { `From: ` }<span className={ `font-medium text-gray-700` }>{ inboundEmail.forwardedFrom || inboundEmail.from }</span>
              { inboundEmail.forwardedFrom ? <span>{ ` (forwarded by ${inboundEmail.from})` }</span> : null }
            </div>
            { inboundEmail.subject ? (
              <div className={ `mt-1 text-sm font-semibold text-gray-900` }>
                { inboundEmail.subject }
              </div>
            ) : null }
            { showOriginalMessageLink && onViewOriginalMessage ? (
              <button
                type={ `button` }
                className={ `mt-1 text-xs text-indigo-700 underline hover:text-indigo-900` }
                onClick={ onViewOriginalMessage }
              >
                { `View original message` }
              </button>
            ) : null }
            { attachments.length > 0 ? (
              <div className={ `mt-2 text-xs text-gray-600` }>
                <span className={ `font-medium text-gray-700` }>{ `Attachments:` }</span>{ ` ` }
                { attachments.map((attachment, index) => (
                  <span key={ `${attachment.index}-${attachment.name}` }>
                    { index > 0 ? `, ` : `` }
                    { onDownloadAttachment ? (
                      <button
                        type={ `button` }
                        className={ `text-indigo-700 underline hover:text-indigo-900 disabled:no-underline disabled:text-gray-500` }
                        onClick={ () => onDownloadAttachment(attachment.index, attachment.name) }
                        disabled={ downloadingAttachmentIndex === attachment.index }
                      >
                        { downloadingAttachmentIndex === attachment.index ? `${attachment.name} (downloading...)` : attachment.name }
                      </button>
                    ) : attachment.name }
                  </span>
                )) }
              </div>
            ) : inboundEmail.attachmentCount > 0 ? (
              <div className={ `mt-2 text-xs text-gray-600` }>
                <span className={ `font-medium text-gray-700` }>{ `Attachments:` }</span>{ ` ` }
                { `${inboundEmail.attachmentCount}` }
              </div>
            ) : null }
            { inboundEmail.matchError ? (
              <div className={ `mt-2 text-xs font-medium text-amber-800` }>
                { `Match error: ${inboundEmail.matchError}` }
              </div>
            ) : null }
            { hasExtractedReplyText ? (
              <div className={ `mt-2 text-sm text-gray-900 whitespace-pre-wrap wrap-break-word` }>
                { inboundEmail.extractedReplyText }
              </div>
            ) : null }
          </div>
          { !hasExtractedReplyText ? (
            <div className={ `px-4 pb-4 text-sm text-gray-500 italic` }>
              { `No extracted reply text available` }
            </div>
          ) : null }
          { showFullEmail && inboundEmail.htmlBody ? (
            <div className={ `border-t border-teal-200` }>
              <button
                type={ `button` }
                aria-expanded={ fullEmailOpen }
                className={ `cursor-pointer px-4 py-2 text-xs text-indigo-700 underline hover:text-indigo-900` }
                onClick={ () => setFullEmailOpen(open => !open) }
              >
                { fullEmailOpen ? `Hide full email` : `Show full email` }
              </button>
              { fullEmailOpen ? (
                <CustomIframe
                  style={ {
                    width: `100%`,
                    minHeight: `270px`,
                    border: `none`,
                    backgroundColor: `white`,
                  } }
                  srcDoc={ inboundEmail.htmlBody }
                />
              ) : null }
            </div>
          ) : null }
        </div>
      </div>
    </div>
  );
}
